// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * KPI tiles computed from the power-flow model and its live state.
 *
 * The retired Knowledge Graph strip summed telemetry by entity type. These
 * tiles sum by *electrical position* instead — the intake boards, the output
 * boards, the mechanical boards — which is what makes them mean something on
 * a one-line: site load is what enters the boards, critical load is what the
 * UPSs deliver, and the difference between them is a loss the operator can
 * check. Each tile carries the rule it was computed by as its caption.
 *
 * A tile with nothing to sum is a gap, not a zero; `value: null` renders as
 * an em-dash with the reason.
 */

import type { KgKpiDef } from '../config/knowledgeGraphKpis';
import type { KgKpiValue } from './kgKpis';
import type { PowerFlowGraph } from './powerFlowGraph';
import type { PowerFlowInference } from './powerFlowInference';
import type { PowerFlowLiveState } from './powerFlowLive';

const def = (id: string, label: string, unit: string, extra: Partial<KgKpiDef> = {}): KgKpiDef => ({
  id,
  label,
  unit,
  aggregate: 'sum',
  sources: [],
  ...extra,
});

/** Sum over the boards that have a reading; `of` is how many boards there are. */
const sum = (
  ids: readonly string[],
  readings: Map<string, number>
): { total: number; n: number; of: number } => {
  let total = 0;
  let n = 0;
  for (const id of ids) {
    const value = readings.get(id);
    if (value === undefined) continue;
    total += value;
    n += 1;
  }
  return { total, n, of: ids.length };
};

/**
 * A sum over some of the boards is not the site's load. The caption says
 * "n of N" and the tile goes amber so a partial total never reads as complete.
 */
const partial = (s: { n: number; of: number }): boolean => s.n < s.of;
const countOf = (s: { n: number; of: number }, noun: string): string =>
  `${partial(s) ? `${s.n} of ${s.of}` : s.n} ${s.of === 1 ? noun : `${noun}s`}`;

const tile = (
  definition: KgKpiDef,
  value: number | null,
  contributingCount: number,
  status: KgKpiValue['status'] = 'ok',
  unavailableReason?: string
): KgKpiValue => ({
  def: definition,
  value,
  contributingCount,
  status: value === null ? 'idle' : status,
  ...(value === null ? { unavailableReason: unavailableReason ?? 'Nothing to sum' } : {}),
});

/**
 * The strip: the four metrics the site asked for (AB#33172), in reading
 * order — facility load, critical load, PUE, UPS state of charge.
 *
 * Critical load is a placeholder for "how much capacity is being used",
 * which the UI does not compute yet; confirm before building on it.
 */
export const computePowerFlowKpis = (
  graph: PowerFlowGraph,
  inference: PowerFlowInference,
  live: PowerFlowLiveState
): KgKpiValue[] => {
  const { vertices } = graph;
  const tiles: KgKpiValue[] = [];

  // Facility load: what enters the intake boards.
  const site = sum(inference.intakeBoardIds, live.powerKwOf);
  tiles.push(
    tile(
      def('pf-site-load', 'Facility load', 'MW', {
        precision: 2,
        caption: `Σ active power at ${countOf(site, 'intake board')}`,
      }),
      site.n > 0 ? site.total / 1000 : null,
      site.n,
      partial(site) ? 'warn' : 'ok',
      'No intake board publishes active power'
    )
  );

  // Critical load: what the output boards carry. PDUs stand in only when the
  // site has no output board at all — a known board with no reading is a gap
  // to report, not a reason to quietly sum something else.
  let criticalIds: readonly string[] = inference.outputBoardIds;
  let critical = sum(criticalIds, live.powerKwOf);
  let criticalBasis = countOf(critical, 'output board');
  if (inference.outputBoardIds.length === 0) {
    criticalIds = vertices.filter(v => v.symbol === 'pdu').map(v => v.id);
    critical = sum(criticalIds, live.powerKwOf);
    criticalBasis = countOf(critical, 'PDU');
  }
  tiles.push(
    tile(
      def('pf-critical-load', 'Critical load', 'MW', {
        precision: 2,
        caption: `Σ active power at ${criticalBasis}`,
      }),
      critical.n > 0 ? critical.total / 1000 : null,
      critical.n,
      partial(critical) ? 'warn' : 'ok',
      'No output board or PDU publishes active power'
    )
  );

  // PUE proxy. The numerator and the denominator must cover the same plant:
  // a ratio of every intake board over every critical board is a PUE proxy,
  // but a ratio of *some* intake boards over every critical board is a number
  // below one that means nothing. So the tile is computed only when every
  // intake board and every critical board on the diagram has a fresh power
  // reading, and is a gap otherwise.
  const fullCoverage =
    inference.intakeBoardIds.length > 0 &&
    criticalIds.length > 0 &&
    site.n === inference.intakeBoardIds.length &&
    critical.n === criticalIds.length;
  const pue = fullCoverage && critical.total > 0 ? site.total / critical.total : null;
  tiles.push(
    tile(
      def('pf-pue-proxy', 'PUE proxy', '', {
        precision: 2,
        caption: 'Facility load ÷ critical load, electrical only, every board read',
        thresholds: { warn: 1.6, fault: 2, direction: 'above' },
      }),
      pue,
      pue === null ? 0 : site.n + critical.n,
      pue !== null && pue >= 2 ? 'fault' : pue !== null && pue >= 1.6 ? 'warn' : 'ok',
      'Needs a reading on every intake and critical board'
    )
  );

  // UPS state of charge.
  const soc = sum(
    vertices.filter(v => v.symbol === 'ups').map(v => v.id),
    live.socOf
  );
  const socMin = Math.min(
    ...vertices
      .filter(v => v.symbol === 'ups')
      .map(v => live.socOf.get(v.id))
      .filter((s): s is number => s !== undefined)
  );
  tiles.push(
    tile(
      def('pf-ups-soc', 'UPS state of charge', '%', {
        precision: 1,
        caption: soc.n > 0 ? `mean of ${soc.n}, min ${socMin.toFixed(1)} %` : undefined,
        thresholds: { warn: 80, fault: 50, direction: 'below' },
      }),
      soc.n > 0 ? soc.total / soc.n : null,
      soc.n,
      soc.n > 0 && socMin < 50 ? 'fault' : soc.n > 0 && socMin < 80 ? 'warn' : 'ok',
      'No UPS publishes state of charge'
    )
  );

  return tiles;
};
